import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { LocationsRadiusFilterOptions } from 'types/FilterOptions';
import { Icon, InputNumber } from 'ui';
import { Form, FormInstance, Input, AutoComplete, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { StyledLocationRadiusSelectorWithAmk } from './style';

interface AMKAddressData {
  lksLat: number;
  lksLong: number;
}

interface AMKSearchResult {
  address: string;
  typ: number;
  zipCode?: string;
  lksLat?: number;
  lksLong?: number;
}

interface AutoCompleteOption {
  value: string;
  label: string;
  data?: AMKAddressData;
  isAmk?: boolean;
}

interface Props {
  locations: LocationsRadiusFilterOptions[] | undefined;
  form: FormInstance;
  name?: string;
  latName?: string;
  longName?: string;
  onChange?: () => void;
}

export const LocationRadiusSelectorWithAmk = ({
  locations,
  form,
  name = 'location_radius',
  latName = 'lks_lat',
  longName = 'lks_long',
  onChange,
}: Props) => {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<AutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [showNoResultsTooltip, setShowNoResultsTooltip] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const AMK_ADRESS_SEARCH = `${window.runConfig.backendUrl}/api/v2/vraa/adress/address-struct`;

  const locationValue = Form.useWatch(name, form);
  const latValue = Form.useWatch(latName, form);
  const longValue = Form.useWatch(longName, form);

  useEffect(() => {
    if (!locationValue && !latValue && !longValue) {
      setSelectedValue(null);
      setSearchValue('');
    }
  }, [locationValue, latValue, longValue]);

  useEffect(() => {
    const locationOptions: AutoCompleteOption[] =
      locations?.map((loc) => ({
        value: loc.name,
        label: loc.name,
        isAmk: false,
      })) || [];

    const filteredLocations = searchValue
      ? locationOptions.filter((opt) =>
          opt.label.toLowerCase().includes(searchValue.toLowerCase())
        )
      : locationOptions;

    if (searchValue.length >= 3) {
      fetchAmkAddresses(searchValue, filteredLocations);
    } else {
      setOptions(filteredLocations);
    }
  }, [searchValue, locations]);

  const fetchAmkAddresses = async (
    query: string,
    filteredLocations: AutoCompleteOption[]
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setOptions(filteredLocations);

    try {
      const response = await fetch(
        `${AMK_ADRESS_SEARCH}?search=${encodeURIComponent(query)}`,
        { signal: abortControllerRef.current.signal }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }

      const data = await response.json();
      const results: AMKSearchResult[] = data.data || data || [];

      const amkOptions: AutoCompleteOption[] = results
        .filter((item) => item.typ === 108)
        .map((item) => {
          const displayAddress = item.zipCode
            ? `${item.address}, ${item.zipCode}`
            : item.address;

          return {
            value: displayAddress,
            label: displayAddress,
            isAmk: true,
            data: {
              lksLat: item.lksLat || 0,
              lksLong: item.lksLong || 0,
            },
          };
        });

      setShowNoResultsTooltip(
        amkOptions.length === 0 && filteredLocations.length === 0
      );
      setOptions([...filteredLocations, ...amkOptions]);
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        setOptions(filteredLocations);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (showNoResultsTooltip) {
      setShowNoResultsTooltip(false);
    }
  };

  const handleSelect = (value: string, option: AutoCompleteOption) => {
    if (option.isAmk && option.data) {
      form.setFieldsValue({
        [name]: null,
        [latName]: option.data.lksLat,
        [longName]: option.data.lksLong,
      });
    } else {
      form.setFieldsValue({
        [name]: value,
        [latName]: null,
        [longName]: null,
      });
    }

    setSelectedValue(value);
    setSearchValue('');
    setShowNoResultsTooltip(false);
    onChange?.();
  };

  const handleClear = () => {
    form.setFieldsValue({
      [name]: null,
      [latName]: null,
      [longName]: null,
      distance_radius: null,
    });
    setSelectedValue(null);
    setSearchValue('');
    setShowNoResultsTooltip(false);
    onChange?.();
  };

  return (
    <StyledLocationRadiusSelectorWithAmk>
      <div className="form-title">
        {intl.formatMessage({ id: 'form.location_title' })}
      </div>

      {selectedValue ? (
        <Input
          value={selectedValue}
          readOnly
          suffix={<CloseOutlined onClick={handleClear} />}
          className="selected-input"
        />
      ) : (
        <Tooltip
          title={intl.formatMessage({
            id: 'listings.location.tooltip',
          })}
          open={showNoResultsTooltip}
          placement="right"
        >
          <AutoComplete
            options={options}
            onSelect={(value, option) => {
              handleSelect(value, option);
              setIsOpen(false);
            }}
            onSearch={handleSearch}
            placeholder={intl.formatMessage({
              id: 'form.select_location_placeholder',
            })}
            className="location-autocomplete"
            allowClear
            onClear={handleClear}
            open={isOpen}
            onDropdownVisibleChange={setIsOpen}
            popupMatchSelectWidth={true}
            notFoundContent={
              searchValue.length >= 3 && isLoading
                ? intl.formatMessage({ id: 'general.loading' })
                : null
            }
          >
            <Input
              suffix={
                isLoading ? (
                  <span className="loading-indicator" />
                ) : (
                  <Icon name={'arrow'} onClick={() => setIsOpen(!isOpen)} />
                )
              }
              onClick={() => setIsOpen(true)}
            />
          </AutoComplete>
        </Tooltip>
      )}

      {selectedValue && (
        <InputNumber
          placeholder={intl.formatMessage({
            id: 'form.select_radius_placeholder',
          })}
          controls={true}
          name="distance_radius"
          type="number"
          addonAfter={'Km'}
          className="location-distance-radius-input"
        />
      )}

      <Form.Item name={name} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={latName} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={longName} hidden>
        <Input />
      </Form.Item>
    </StyledLocationRadiusSelectorWithAmk>
  );
};
